import { computed, inject, Injectable } from '@angular/core';
import { ServiceGridItemObject } from '@it2go/types';
import { ServicesDashboardWidgetsEnum } from '@libs/types/src/services/enum/services-dashboard-widgets.enum';
import { CONTEXT_TYPE_WIDGETS_SETTINGS } from '@libs/types/src/services/types/service-context-type-settings.type';
import {
    ServiceDashboardUserSettings,
} from '@libs/types/src/services/types/service-dashboard-user-settings.type';
import {
    SERVICE_SETTINGS_USER_WIDGETS_TYPE_DEFAULT,
    SERVICE_SETTINGS_USER_WIDGETS_TYPE_KEY,
    ServiceSettingsUserWidgetsType,
    UserWidgetSetting,
} from '@libs/types/src/services/types/service-settings-user-widgets.type';
import { featureOn } from '../../../../feature-flag';
import { LoggedUserGlobalStore } from '../../../../store/logged-user.global-store';
import { OrganizationGlobalStore } from '../../../../store/organization.global-store';
import { ServiceDashboardWidgetEntry, ServiceDashboardWidgetsData } from './service-dashboard-widgets.types';
import {
    filterDashboardWidgetsByUserVisibility,
    isDashboardWidgetHideable,
    parseServiceDashboardUserSettings,
} from './service-dashboard-widgets.utils';
import {
    isServiceDashboardWidgetAllowedByAcl,
    isServiceDashboardWidgetAvailableForService,
} from './service-dashboard-widget-policies';

@Injectable({ providedIn: 'root' })
export class ServiceDashboardWidgetsService {
    private readonly loggedUserStore = inject(LoggedUserGlobalStore);
    private readonly organizationStore = inject(OrganizationGlobalStore);

    public readonly configuredWidgetsData = computed<ServiceDashboardWidgetsData>(() => {
        if (!featureOn.SOCIAL_SERVICE_DASHBOARD) {
            return this.getLegacyWidgetsData();
        }

        const widgetsData: ServiceDashboardWidgetsData = {};

        this.organizationStore.services().forEach(service => {
            const availableWidgets = CONTEXT_TYPE_WIDGETS_SETTINGS[service.type as keyof typeof CONTEXT_TYPE_WIDGETS_SETTINGS];

            Object.keys(availableWidgets).forEach(name => {
                const widgetName = name as ServicesDashboardWidgetsEnum;
                const widget = this.getWidgetSetting(service, widgetName);
                if (!widget?.active || !isServiceDashboardWidgetAvailableForService(widgetName, service)) return;

                const entry: ServiceDashboardWidgetEntry = {
                    serviceId: String(service.id),
                    serviceSettings: service.serviceSettings,
                    widget,
                };
                widgetsData[widgetName] = [...(widgetsData[widgetName] ?? []), entry];
            });
        });

        return widgetsData;
    });

    public readonly widgetsData = computed<ServiceDashboardWidgetsData>(() => {
        if (!featureOn.SOCIAL_SERVICE_DASHBOARD) return this.configuredWidgetsData();

        const rules = this.loggedUserStore.rules();

        return Object.fromEntries(
            Object.entries(this.configuredWidgetsData()).flatMap(([name, entries]) => {
                const widgetName = name as ServicesDashboardWidgetsEnum;
                const eligibleEntries = entries.filter(entry => (
                    isServiceDashboardWidgetAllowedByAcl(widgetName, Number(entry.serviceId), rules)
                    && this.loggedUserStore.isInAnyGroupForService(
                        entry.widget.visibleForGroupIds ?? [],
                        Number(entry.serviceId),
                    )
                ));

                return eligibleEntries.length ? [[widgetName, eligibleEntries]] : [];
            }),
        ) as ServiceDashboardWidgetsData;
    });

    public readonly hideableWidgetNames = computed<ServicesDashboardWidgetsEnum[]>(() => {
        if (!featureOn.SOCIAL_SERVICE_DASHBOARD) return [];

        return Object.entries(this.widgetsData())
            .filter(([, entries]) => isDashboardWidgetHideable(entries))
            .map(([name]) => name as ServicesDashboardWidgetsEnum);
    });

    public readonly hiddenWidgetNames = computed<ServicesDashboardWidgetsEnum[]>(() => {
        if (!featureOn.SOCIAL_SERVICE_DASHBOARD) return [];

        const organizationId = this.organizationStore.selectedId();
        if (!organizationId) return [];

        return this.getUserSettings().hiddenWidgetsByOrganization[String(organizationId)] ?? [];
    });

    public readonly visibleWidgetsData = computed<ServiceDashboardWidgetsData>(() => {
        if (!featureOn.SOCIAL_SERVICE_DASHBOARD) return this.widgetsData();

        return filterDashboardWidgetsByUserVisibility(
            this.widgetsData(),
            this.hiddenWidgetNames(),
        );
    });

    public getUserSettings(): ServiceDashboardUserSettings {
        return parseServiceDashboardUserSettings(this.loggedUserStore.customSettings());
    }

    private getLegacyWidgetsData(): ServiceDashboardWidgetsData {
        const widgetsData: ServiceDashboardWidgetsData = {};
        const widgetsAllContexts = {
            ...CONTEXT_TYPE_WIDGETS_SETTINGS.healthCare,
            ...CONTEXT_TYPE_WIDGETS_SETTINGS.social,
        };

        Object.entries(widgetsAllContexts).forEach(([name, settingKey]) => {
            const widgetName = name as ServicesDashboardWidgetsEnum;
            const services = this.loggedUserStore.activeServicesForSettingAndWidget(settingKey, widgetName);
            services.forEach(service => {
                const widget = this.getWidgetSetting(service, widgetName);
                if (!widget) return;

                const entry: ServiceDashboardWidgetEntry = {
                    serviceId: String(service.id),
                    serviceSettings: service.serviceSettings,
                    widget,
                };
                widgetsData[widgetName] = [...(widgetsData[widgetName] ?? []), entry];
            });
        });

        return widgetsData;
    }

    private getWidgetSetting(
        service: ServiceGridItemObject,
        widgetName: ServicesDashboardWidgetsEnum,
    ): UserWidgetSetting | undefined {
        const defaultWidget = SERVICE_SETTINGS_USER_WIDGETS_TYPE_DEFAULT.widgets.find(
            widget => widget.name === widgetName,
        );
        if (!defaultWidget) return undefined;

        const storedSetting = service.serviceSettings.find(
            setting => setting.key === SERVICE_SETTINGS_USER_WIDGETS_TYPE_KEY,
        );
        const storedWidget = storedSetting
            ? (JSON.parse(storedSetting.data) as ServiceSettingsUserWidgetsType).widgets
                ?.find(widget => widget.name === widgetName)
            : undefined;

        return {
            ...defaultWidget,
            ...storedWidget,
            allowHide: storedWidget?.allowHide ?? false,
            visibleForGroupIds: storedWidget?.visibleForGroupIds ?? [],
        };
    }

}
